import { ChangeEvent } from 'react';

import {
  ChakraAvatar,
  ChakraBadge,
  ChakraFlex,
  ChakraText,
  Checkbox,
  EquipUIFireflyV2Theme,
  EquipUITheme,
} from '@equip.health/ui';

const { neutral, primary } = EquipUIFireflyV2Theme.colors;
const { captionBold, label, paragraph } = EquipUITheme.typography;

const TOP_LEVEL_ID = 'participant';

const readOnlyStylesMapper: Record<
  'true' | 'false',
  Record<string, string | Record<'true' | 'false', string>>
> = {
  false: {
    alignItems: 'center',
    avatarPaddingRight: '16px',
    backgroundColor: 'primary.10',
    border: {
      false: `1px solid ${neutral.line}`,
      true: `1px solid ${primary[100]}`,
    },
    justifyContent: 'space-between',
    padding: '12px 14px',
  },
  true: {
    alignItems: 'flex-start',
    avatarPaddingRight: '12px',
    backgroundColor: 'neutral.background.primary',
    border: 'none',
    justifyContent: 'flex-start',
    padding: '0px',
  },
};

interface ParticipantProps {
  id: string;
  isChecked: boolean;
  isDisabled?: boolean;
  isHost?: boolean;
  isReadOnly?: boolean;
  moreCount?: number;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  primaryText: string;
  secondaryText: string;
}

const Participant = ({
  id,
  isChecked,
  isDisabled = false,
  isHost = false,
  isReadOnly = false,
  moreCount = 0,
  onChange,
  primaryText,
  secondaryText,
}: ParticipantProps) => {
  const readOnly = `${isReadOnly}`;

  return isReadOnly && !isChecked ? null : (
    <ChakraFlex
      backgroundColor={
        isChecked && readOnlyStylesMapper[readOnly].backgroundColor
      }
      border={readOnlyStylesMapper[readOnly].border[`${isChecked}`]}
      borderRadius="12px"
      marginBottom="8px"
      padding={readOnlyStylesMapper[readOnly].padding}
    >
      <ChakraFlex
        align={readOnlyStylesMapper[readOnly].alignItems}
        justify={readOnlyStylesMapper[readOnly].justifyContent}
        width="100%"
      >
        <ChakraFlex align="center" width="100%">
          <ChakraAvatar
            background="secondary.50"
            float="right"
            fontSize="small"
            height="28px"
            icon={
              <ChakraFlex alignItems="center" justifyContent="center">
                <ChakraText color="neutral.background.primary" fontSize="10px">
                  {primaryText
                    ?.replace('"', '')
                    .split(' ')
                    .map((name: string) => name?.[0]?.toUpperCase() ?? '')
                    .join('')}
                </ChakraText>
              </ChakraFlex>
            }
            id={`${TOP_LEVEL_ID}__${id}__user-avatar-card__avatar`}
            overflow="hidden"
            width="28px"
          />
          <ChakraFlex
            direction="column"
            paddingLeft={readOnlyStylesMapper[readOnly].avatarPaddingRight}
            width="70%"
          >
            <ChakraText
              {...paragraph}
              id={`${TOP_LEVEL_ID}__${id}__name`}
              lineHeight="19.6px"
            >
              {primaryText}
            </ChakraText>
            <ChakraText
              {...label}
              color="neutral.secondary"
              id={`${TOP_LEVEL_ID}__${id}__role`}
              lineHeight="16.8px"
            >
              {secondaryText}
            </ChakraText>
          </ChakraFlex>
          {moreCount > 0 && (
            <ChakraFlex
              alignItems="center"
              background={neutral.background.secondary}
              borderRadius="100%"
              height="24px"
              justifyContent="center"
              width="24px"
              {...captionBold}
            >
              +{moreCount}
            </ChakraFlex>
          )}
        </ChakraFlex>
        {isHost && (
          <ChakraBadge
            {...captionBold}
            backgroundColor="neutral.background.secondary"
            borderRadius="100px"
            color="neutral.secondary"
            id={`${TOP_LEVEL_ID}__${id}__host-badge`}
            marginX="6px"
            padding="4px 8px"
            textTransform="capitalize"
          >
            Host
          </ChakraBadge>
        )}
        {!isReadOnly && (
          <Checkbox
            id={`${TOP_LEVEL_ID}__${id}__is-attending`}
            isChecked={isChecked}
            isDisabled={isDisabled}
            marginBottom="-4px"
            onChange={onChange}
          />
        )}
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default Participant;
